import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import type { Campaign, AutomationRules } from "@type/agent";
import {
    getAgentCampaign,
    updateAgentCampaign,
    updateCampaignFlow,
    updateCampaignEvaluation,
    updateCampaignAutomation,
    updateCampaignStatus,
    getCampaignContacts,
    initiateImmediateCall,
    initiateScheduledCall,
} from "@service/agent";
import { Loader2 } from "lucide-react";
import { withRetry } from "@util/retry";
import CampaignFormContainer from "./campaign-form";

export default function EditCampaignContainer() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCampaign = async () => {
            if (!id) return;
            setLoading(true);
            const data = await getAgentCampaign(id);
            if (data) {
                setCampaign(data);
            } else {
                navigate("/agent/call-setup");
            }
            setLoading(false);
        };
        fetchCampaign();
    }, [id, navigate]);

    const saveCampaignConfig = async (updated: Campaign) => {
        if (!id) return;
        // Each write is retried independently so a single transient failure among
        // the parallel PUTs doesn't reject the whole save and leave the user unsure
        // what persisted.
        await Promise.all([
            withRetry(() => updateAgentCampaign(id, updated)),
            withRetry(() => updateCampaignFlow(id, {
                openingScript: updated.openingScript,
                closingScript: updated.closingScript,
                coreQuestions: updated.coreQuestions,
                conditionalLogic: updated.conditionalLogic,
                conversationMode: updated.conversationMode,
                objective: updated.objective,
                conversationGoal: updated.conversationGoal,
                topicAreas: updated.topicAreas,
                probingDepth: updated.probingDepth,
                guardrails: updated.guardrails,
            })),
            withRetry(() => updateCampaignEvaluation(id, updated.evaluationCriteria)),
            withRetry(() => updateCampaignAutomation(id, {
                triggerMethod: updated.triggerMethod,
                retryAttempts: updated.retryAttempts,
                retryDelayMinutes: updated.retryDelayMinutes,
                webhookUrl: updated.webhookUrl,
                apiEndpoint: updated.apiEndpoint,
                scheduleConfig: updated.scheduleConfig as unknown as AutomationRules["scheduleConfig"],
            })),
        ]);
        // Refetch authoritative server state so the form reflects what was actually
        // persisted. Without this the local form state and server can silently drift,
        // which made repeated edits appear to "only save once".
        const fresh = await getAgentCampaign(id);
        if (fresh) setCampaign(fresh);
    };

    const handleActivate = async (updated: Campaign) => {
        if (!id) return;

        // 1. Save all config
        await saveCampaignConfig(updated);

        // 2. Change status to active
        await updateCampaignStatus(id, "active");

        // 3. Initiate calls based on timing mode
        const scheduleConfig = updated.scheduleConfig as Record<string, unknown> | undefined;
        const timingMode = (scheduleConfig?.timingMode as string) || "immediate";

        if (timingMode === "immediate" || timingMode === "scheduled") {
            const contactList = await getCampaignContacts(id);
            const callableIds = contactList.contacts
                .filter((c) => c.status !== "called")
                .map((c) => c.id);

            if (callableIds.length === 0) {
                throw new Error("No callable contacts found. All contacts have already been called.");
            }

            if (timingMode === "immediate") {
                await initiateImmediateCall(id, callableIds);
            } else {
                const scheduledDate = scheduleConfig?.scheduledDate as string;
                const scheduledTime = scheduleConfig?.scheduledTime as string;
                if (!scheduledDate || !scheduledTime) {
                    throw new Error("Scheduled date and time are required for scheduled calls.");
                }
                await initiateScheduledCall(id, callableIds, scheduledDate, scheduledTime);
            }
        }

        navigate("/agent/call-setup");
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20 text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin" />
            </div>
        );
    }

    if (!campaign) return null;

    return <CampaignFormContainer initialCampaign={campaign} onSave={saveCampaignConfig} onActivate={handleActivate} />;
}
